import React, { useCallback, useEffect } from 'react'
import { useFormik } from 'formik'
import {
  AlertProps,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { FileWithPath } from 'react-dropzone'
import { Body1, H5Bold, H6, H6Bold } from '../../components/Typography'
import { Box } from '../../components/Box'
import { Flex } from '../../components/Flex'
import { ButtonGreen } from '../../components/Buttons'
import {
  FileError,
  FileErrorType,
  FileType,
  GetFinancialYearsDocument,
  GetSieFilesDocument,
  GetSupplierRegistryFileDocument,
  useGetFinancialYearsQuery,
  useGetSieFilesQuery,
  useGetSupplierRegistryFileQuery,
  useValidateAndRegisterFileMutation,
} from '../../graphql/generated'
import { colors, spacing } from '../../theme'
import { captureException } from '../../utils/Sentry'
import { Dropzone } from '../../components/Dropzone'
import { UploadInfo, useFileUploadState } from '../../hooks/useFileUploadState'
import { parseYear } from '../../utils/date'

class HandledError extends Error {}

interface Props {
  setSnackbar: React.Dispatch<
    React.SetStateAction<Pick<AlertProps, 'children' | 'severity'> | null>
  >
  onSuccess?: () => void
  enforceSIEFile?: boolean
}

export function SIEFileUpload({
  setSnackbar,
  onSuccess,
  enforceSIEFile,
}: Props): React.JSX.Element {
  const [validateAndRegisterFile] = useValidateAndRegisterFileMutation({
    fetchPolicy: 'no-cache',
    refetchQueries: [
      { query: GetSieFilesDocument },
      { query: GetSupplierRegistryFileDocument },
      { query: GetFinancialYearsDocument },
    ],
  })

  const {
    files: sieFiles,
    uploadProgress,
    addFilesToMap: addSIEFies,
    removeFileFromMap: removeSIEFile,
    setUploadProgress,
    removeFiles: removeSieFiles,
    computeOverallProgress,
    uploadFile,
  } = useFileUploadState()

  const setError = useCallback(
    (error: string) => {
      if (error) {
        setSnackbar({
          children: error,
          severity: 'error',
        })
      } else {
        setSnackbar(null)
      }
    },
    [setSnackbar],
  )

  const setSuccess = () => {
    setSnackbar({
      severity: 'success',
      children: 'Filerna har laddats upp.',
    })
  }

  const [supplierRegistry, setSupplierRegistry] = React.useState<
    FileWithPath | undefined
  >()
  const addSupplierRegistry = useCallback((files: readonly FileWithPath[]) => {
    setSupplierRegistry(files[0])
  }, [])

  const removeSupplierRegistry = useCallback(() => {
    setSupplierRegistry(undefined)
  }, [])

  const { data: getSieFilesData, error: getSieFilesError } =
    useGetSieFilesQuery()
  const { data: getSupplierRegistryData, error: getSupplierRegistryError } =
    useGetSupplierRegistryFileQuery()
  const { data: financialYearData, error: financialYearError } =
    useGetFinancialYearsQuery()

  useEffect(() => {
    if (getSieFilesError || getSupplierRegistryError || financialYearError) {
      setError('Något gick fel. Ladda om sidan.')
    }
  }, [getSieFilesError, getSupplierRegistryError, setError, financialYearError])

  type FormValues = {
    siefiles?: FileWithPath[]
    supplierRegistry?: FileWithPath
  }

  const initialValues: FormValues = {
    siefiles: [],
    supplierRegistry: undefined,
  }

  const handleCheckError = (
    err: {
      __typename?: 'FileError'
    } & Pick<FileError, 'message' | 'errorType'>,
    fileType: FileType,
  ) => {
    const fileTypeString =
      fileType === FileType.Sie ? 'SIE' : 'leverantörsregistret'
    captureException(new Error(`${err.errorType} - ${err.message}`), 'info')
    switch (err.errorType) {
      case FileErrorType.TooBig:
        setError(
          'En av filerna du försökte ladda upp är för stor. Vänligen kontakta oss så att vi kan utarbeta en lösning för ditt fall.',
        )
        break
      case FileErrorType.Malformed:
        setError(
          `Vi kunde inte läsa en av ${fileTypeString}-filerna du försökte ladda upp. Se till att dina filer är i rätt format.`,
        )
        break
      case FileErrorType.MissingColumn:
        setError(
          'Vi kunde inte hitta kolumnerna supplierName och supplierOrgnr i leverantörsregistret. Se till att dina filer är i rätt format.',
        )
        break
      case FileErrorType.MissingRar:
      case FileErrorType.MissingYear:
        setError(
          'En av SIE-filerna du laddade upp saknar information om vilket år den täcker. Se till att din SIE-fil är korrekt formaterad.',
        )
        break
      case FileErrorType.NoSheet:
      case FileErrorType.NoData:
        setError(`En av ${fileTypeString}-filerna innehåller ingen data.`)
        break
      case FileErrorType.OutOfSubscription:
        setError(
          'En av SIE-filerna du laddade upp är för ett år som inte täcks av din prenumeration. Kontakta oss om du vill utöka din prenumeration.',
        )
        break
      default:
        setError('Något gick fel vid uppladdningen, försök igen.')
    }
  }

  const uploadeSIEFile = async (files: FileWithPath[]) => {
    const uploadPromises = files.map(uploadFile)
    const fileInfos = await Promise.all(uploadPromises)

    const successFiles: FileWithPath[] = []

    const checkPromises = fileInfos.map(async (fileInfo) => {
      const checkResult = await validateAndRegisterFile({
        variables: {
          fileId: fileInfo.id,
          fileType: FileType.Sie,
          fileName: fileInfo.file.name,
        },
      })
      if (checkResult.data?.validateAndRegisterFile.error) {
        handleCheckError(
          checkResult.data.validateAndRegisterFile.error,
          FileType.Sie,
        )
        throw new HandledError('some files failed')
      } else {
        successFiles.push(fileInfo.file)
      }
    })
    const results = await Promise.allSettled(checkPromises)
    if (successFiles.length > 0) {
      removeSieFiles(successFiles)
    }

    const rejected = results.find(
      (r): r is PromiseRejectedResult => r.status === 'rejected',
    )
    if (rejected) {
      throw rejected.reason
    }
  }

  const uploadeSupplierRegistry = async (
    supplierReg: FileWithPath,
    index: number,
  ) => {
    const fileInfo = await uploadFile(supplierReg, index)
    const checkResult = await validateAndRegisterFile({
      variables: {
        fileId: fileInfo.id,
        fileType: FileType.SupplierRegistry,
        fileName: supplierReg.name,
      },
    })
    if (checkResult.data?.validateAndRegisterFile.error) {
      handleCheckError(
        checkResult.data.validateAndRegisterFile.error,
        FileType.SupplierRegistry,
      )
      throw new HandledError('some files failed')
    }
    setSupplierRegistry(undefined)
  }

  const { setFieldValue, handleSubmit } = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setError('')
      const initialProgress: UploadInfo[] = []
      if (values.siefiles) {
        values.siefiles.forEach((file) =>
          initialProgress.push({ loaded: 0, total: file.size }),
        )
      }
      if (values.supplierRegistry) {
        initialProgress.push({
          loaded: 0,
          total: values.supplierRegistry.size,
        })
      }
      setUploadProgress(initialProgress)

      try {
        const sieFileCount = values.siefiles?.length ?? 0

        const promises: Promise<void>[] = []

        if (values.siefiles && values.siefiles.length > 0) {
          promises.push(uploadeSIEFile(values.siefiles))
        }

        if (values.supplierRegistry) {
          // The index for supplierRegistry should be after all sieFiles
          const supplierRegistryIndex = sieFileCount
          promises.push(
            uploadeSupplierRegistry(
              values.supplierRegistry,
              supplierRegistryIndex,
            ),
          )
        }

        const results = await Promise.allSettled(promises)
        const rejected = results.find(
          (r): r is PromiseRejectedResult => r.status === 'rejected',
        )
        if (rejected) {
          throw rejected.reason
        }

        if (onSuccess) {
          onSuccess()
        } else {
          setSuccess()
        }
      } catch (err) {
        if (!(err instanceof HandledError)) {
          captureException(err)
          setError('Något gick fel vid uppladdningen, försök igen')
        }
      }

      setUploadProgress([])
    },
  })

  useEffect(() => {
    setFieldValue('siefiles', Array.from(sieFiles.values()))
  }, [setFieldValue, sieFiles])

  useEffect(() => {
    setFieldValue('supplierRegistry', supplierRegistry)
  }, [setFieldValue, supplierRegistry])

  const getYear = (financialYearId: number) => {
    const financialYear = financialYearData?.getFinancialYears.find(
      (fy) => fy.id === financialYearId,
    )
    if (!financialYear) {
      return financialYearId
    }
    return parseYear(financialYear.from)
  }

  return (
    <Box style={{ width: '100%' }}>
      <Flex
        column
        stretchWidth
        style={{ padding: spacing.large, gap: spacing.large }}
      >
        <H5Bold data-testid='sie-upload-header'>Ladda upp filer</H5Bold>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Flex column style={{ gap: spacing.large }}>
            <Flex column stretchWidth style={{ gap: spacing.small }}>
              <H6Bold>SIE-filer</H6Bold>
              <Dropzone
                text='Dra och släpp SIE-filer här, eller klicka för att välja filer'
                fileTypes={['.se']}
                fileTypesKey='text/*'
                files={[...sieFiles.values()]}
                addFiles={addSIEFies}
                removeFile={removeSIEFile}
                multiple
              />
              {getSieFilesData?.getSIEFiles &&
                getSieFilesData?.getSIEFiles.length > 0 && (
                  <>
                    <H6>Befintliga SIE-filer</H6>
                    <TableContainer>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell>År</TableCell>
                            <TableCell>Namn</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSieFilesData?.getSIEFiles.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>
                                {getYear(row.financialYearId)}
                              </TableCell>
                              <TableCell>{row.fileName}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
            </Flex>
            <Flex column stretchWidth style={{ gap: spacing.small }}>
              <H6Bold>leverantörsregistret</H6Bold>
              <Dropzone
                text='Dra och släpp leverantörsregistret här. Eller klicka för att välja en fil'
                fileTypes={['.xlsx']}
                fileTypesKey='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                files={supplierRegistry ? [supplierRegistry] : []}
                addFiles={addSupplierRegistry}
                removeFile={removeSupplierRegistry}
                multiple={false}
              />
              {getSupplierRegistryData?.getSupplierRegistryFile && (
                <>
                  <H6>Befintligt leverantörsregister</H6>
                  <Body1>
                    {getSupplierRegistryData?.getSupplierRegistryFile.fileName}
                  </Body1>
                </>
              )}
            </Flex>
            {/* 
              If we are in profile creation (onSuccess is set), and
              there are no files pending upload, and the org has
              already uploaded SIE files, then allow them to skip ahead.
             */}
            {onSuccess &&
            sieFiles.size === 0 &&
            !supplierRegistry &&
            getSieFilesData?.getSIEFiles.length ? (
              <ButtonGreen onClick={() => onSuccess()}>Nästa</ButtonGreen>
            ) : (
              <ButtonGreen
                type='submit'
                endIcon={
                  uploadProgress.length > 0 && (
                    <CircularProgress
                      variant='determinate'
                      value={computeOverallProgress()}
                      style={{ color: colors.white }}
                      size={20}
                    />
                  )
                }
                disabled={
                  uploadProgress.length > 0 ||
                  (sieFiles.size === 0 && !supplierRegistry) ||
                  (enforceSIEFile && sieFiles.size === 0)
                }
                data-testid='submit-sie-upload'
              >
                Ladda upp
              </ButtonGreen>
            )}
          </Flex>
        </form>
      </Flex>
    </Box>
  )
}
